import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { DateTimePicker, Modal } from '@/components';
import { Button } from '@/components/Button';
import { Input } from '@/components/Inputs';
import { offsetDate, snapToNextSlot } from '@/utils/dateSlots';

import { RichTextArea } from '../../../../RichTextArea/RichTextArea';
import styles from '../../../CreateSheet.module.scss';
import type { SubEvent } from '../ItinerarySection';

const schema = z
  .object({
    name: z.string().min(1, 'Please enter a name.'),
    description: z.string().optional(),
    startDateTime: z.string().min(1, 'Please choose a start date and time.'),
    endDateTime: z.string().min(1, 'Please enter an end date and time.'),
  })
  .refine(
    (data) => {
      const start = new Date(data.startDateTime).getTime();
      const end = new Date(data.endDateTime).getTime();
      if (Number.isNaN(start) || Number.isNaN(end)) return true;
      return end >= start;
    },
    { message: 'The end time is before the start time.', path: ['endDateTime'] },
  );

type FormValues = z.infer<typeof schema>;

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: (subEvent: SubEvent) => void;
  initialData?: SubEvent;
};

function getDefaultValues(initialData?: SubEvent): FormValues {
  if (initialData) {
    const start = snapToNextSlot();
    const fallbackEnd = offsetDate(start, 15).toISOString();
    return {
      name: initialData.title,
      description: initialData.description,
      startDateTime: initialData.time_slot?.start || start.toISOString(),
      endDateTime: initialData.time_slot?.end || fallbackEnd,
    };
  }
  const start = snapToNextSlot();
  return {
    name: '',
    description: '',
    startDateTime: start.toISOString(),
    endDateTime: offsetDate(start, 15).toISOString(),
  };
}

function CreateItinerarySheet({ open, onClose, onSubmit, initialData }: Props) {
  const {
    register,
    control,
    handleSubmit,
    setValue,
    trigger,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: getDefaultValues(initialData),
  });

  const startDateValue = useWatch({ control, name: 'startDateTime' });
  const endDateValue = useWatch({ control, name: 'endDateTime' });

  useEffect(() => {
    void trigger('endDateTime');
  }, [startDateValue, trigger]);

  useEffect(() => {
    if (open) reset(getDefaultValues(initialData));
  }, [open, initialData, reset]);

  const onFormSubmit = (values: FormValues) => {
    onSubmit({
      title: values.name,
      description: values.description ?? '',
      time_slot: {
        start: values.startDateTime,
        end: values.endDateTime,
      },
      participants: initialData?.participants ?? [],
    });
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title={initialData ? 'Edit Itinerary' : 'Add Itinerary'}>
      <form
        className={styles.form}
        onSubmit={(e) => {
          e.stopPropagation();
          void handleSubmit(onFormSubmit)(e);
        }}
      >
        <Input
          label="Name"
          placeholder="Name"
          mandatory
          error={errors.name?.message}
          {...register('name')}
        />
        <DateTimePicker
          label="Dates"
          placeholder="Choose dates"
          mandatory
          endRequired
          value={startDateValue || null}
          endValue={endDateValue || null}
          onRangeChange={(start, end) => {
            setValue('startDateTime', start ?? '', { shouldValidate: true });
            setValue('endDateTime', end ?? '', { shouldValidate: true });
          }}
          error={errors.endDateTime?.message ?? errors.startDateTime?.message}
        />
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <RichTextArea
              label="Description"
              placeholder="Description"
              value={field.value ?? ''}
              onChange={field.onChange}
            />
          )}
        />
        <Button type="submit">{initialData ? 'Save' : 'Add'}</Button>
      </form>
    </Modal>
  );
}

export default CreateItinerarySheet;
